import { ReactNode, useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { ErrorType } from '@/components/signup/hooks/use-site-name-validation';
import { isProduction } from '@/components/signup/utils/environment';
import { fontSize } from '@/components/signup/utils/font-size';
import { wacToken } from '@/libraries/design/foundations';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { useRecommendedSiteName } from '../../hooks/recommended-site-name';
import { useSiteActivationContext } from '../../hooks/site-activation-context';
import { messages as validationMessages } from '../../messages';

import { IsValidOrLoading } from './is-valid-or-loading';
import { Label } from './label';

type InputSiteNameProps = {
	inputLabel?: ReactNode;
};

export const SiteNameInput = ({ inputLabel }: InputSiteNameProps) => {
	useRecommendedSiteName({ onError: console.error }); // onError catches errors from endpoint

	const {
		siteName,
		setSiteNameValue,
		isSubmitting,
		validationMessageState: [validationMessage, setValidationMessage],
		isValid,
		isValidating,
		error,
		isWorkspaceProduct,
	} = useSiteActivationContext();

	const siteNameAddressSuffix = isProduction() ? '.atlassian.net' : '.jira-dev.com';

	const visualFeedback = checkFeatureFlagGate(featureFlags.signupVisualAlignmentFeedback);

	useEffect(() => {
		switch (error) {
			case ErrorType.NO_ERROR:
				setValidationMessage(undefined);
				break;
			case ErrorType.INVALID_NAME:
				setValidationMessage(validationMessages.invalidName);
				break;
			case ErrorType.MAXIMUM_LENGTH:
				setValidationMessage(validationMessages.maxLength);
				break;
			case ErrorType.UNAVAILABLE:
				setValidationMessage(validationMessages.unavailable);
				break;
			case ErrorType.NETWORK_ERROR:
				setValidationMessage(validationMessages.networkError);
				break;
		}
	}, [error, setValidationMessage]);

	const label =
		(inputLabel ?? isWorkspaceProduct) ? (
			<FormattedMessage id="siteActivation.yourWorkspace" defaultMessage="Your workspace" />
		) : (
			<FormattedMessage
				id="accountActivation.nameSite.inputSiteNameLabel"
				defaultMessage="Your site"
			/>
		);

	const ariaSuffixId = 'site-name-suffix';
	const ariaHelperId = 'site-name-helper';
	const ariaErrorId = 'site-name-error';

	const [helperPresent, setHelperPresent] = useState(false);
	useEffect(() => {
		// Detect if helper text is rendered on the page to avoid invalid aria-describedby refs
		if (typeof document !== 'undefined') {
			setHelperPresent(!!document.getElementById(ariaHelperId));
		}
	}, []);

	const textFieldAriaDescribedBy =
		[
			ariaSuffixId,
			helperPresent ? ariaHelperId : undefined,
			validationMessage ? ariaErrorId : undefined,
		]
			.filter(Boolean)
			.join(' ') || undefined;

	return (
		<div data-testid="adg4-site-name-input" css={labelCss}>
			<div css={[fieldWrapperCss, visualFeedback && fieldWrapperVisualFeedbackCss]}>
				<Field
					aria-required={true}
					isDisabled={isSubmitting}
					name="site-name"
					label={<Label>{label}</Label>}
				>
					{({ fieldProps }) => (
						<div
							css={[isValid && inputValidCss, validationMessage !== undefined && inputInvalidCss]}
						>
							<TextField
								type="text"
								autoComplete="on"
								testId="site-name-input"
								{...fieldProps}
								value={siteName}
								onChange={(e) => {
									setSiteNameValue(e.currentTarget.value);
									fieldProps.onChange(e);
								}}
								aria-describedby={textFieldAriaDescribedBy}
								elemAfterInput={
									<div css={siteNameAfterCss}>
										<span id={ariaSuffixId} css={siteNameAfterLabelV2Css}>
											{siteNameAddressSuffix}
										</span>
										<IsValidOrLoading
											isValidating={isValidating}
											siteNameValue={siteName}
											validationMessage={validationMessage}
										/>
									</div>
								}
							/>
							<div
								css={[siteNameErrorCss, validationMessage && siteNameErrorVisibleCss]}
								id={ariaErrorId}
								role="alert"
							>
								{validationMessage && <FormattedMessage {...validationMessage} />}
							</div>
						</div>
					)}
				</Field>
			</div>
		</div>
	);
};

const inputInvalidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
	['div[data-ds--text-field--container]']: {
		borderColor: `${token('color.border.danger')} !important`,
	},
});

const inputValidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
	['div[data-ds--text-field--container]']: {
		borderColor: `${token('color.border.success')} !important`,
	},
});

const fieldWrapperCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		backgroundColor: `${token('color.background.input')} !important`,
		borderRadius: '21px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	input: {
		backgroundColor: `${token('color.background.input')} !important`,
		flex: 1,
		fontFamily: 'inherit',
		fontSize: '1rem',
		paddingInline: `${token('space.150')} !important`,
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	["span[title='required']"]: {
		display: 'none',
	},
});

const fieldWrapperVisualFeedbackCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderRadius: '6px !important', // remove !important when gate ships and objects are merged
	},
});

const labelCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	label: {
		fontFamily: 'inherit',
	},
});

const siteNameAfterCss = css({
	alignItems: 'center',
	display: 'flex',
	paddingInlineEnd: 10,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	svg: {
		width: 20,
		height: 20,
	},
});

const siteNameAfterLabelV2Css = css({
	color: token('color.text.subtlest'),
	fontSize: fontSize.sm,
	paddingInlineEnd: token('space.100'),
	whiteSpace: 'nowrap',
});

const siteNameErrorCss = css({
	color: token('color.text.danger'),
	fontSize: '0.75rem',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		color: wacToken('color.primary.blue'),
		textDecoration: 'underline',
	},
});

const siteNameErrorVisibleCss = css({
	marginBlockStart: 6,
});
